import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Loader2, GraduationCap, UserCog, ArrowRight, ChevronRight } from "lucide-react";
import { StudentsAPI, StaffAPI } from "@/lib/sprint3-api";
import { MODULE_GROUPS } from "@/lib/modules";

// Flatten the sidebar into a quick-jump command list (enabled modules only).
const MODULE_INDEX = MODULE_GROUPS.flatMap((g) =>
  g.items.filter((i) => i.enabled).map((i) => ({ ...i, group: g.label }))
);

export function GlobalSearch() {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const boxRef = useRef(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [debounced, setDebounced] = useState("");
  const [students, setStudents] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(false);

  // Debounce keystrokes — only hit the API after the user pauses typing.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    if (!debounced) {
      setStudents([]); setStaff([]); setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    Promise.allSettled([
      StudentsAPI.list({ q: debounced, limit: 6 }),
      StaffAPI.list({ q: debounced, limit: 6 }),
    ]).then(([s1, s2]) => {
      if (cancelled) return;
      setStudents(s1.status === "fulfilled" ? (s1.value || []).slice(0, 6) : []);
      setStaff(s2.status === "fulfilled" ? (s2.value || []).slice(0, 6) : []);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [debounced]);

  // ⌘K / Ctrl+K to focus, Esc to close.
  useEffect(() => {
    const onKey = (e) => {
      const isMac = navigator.platform.toLowerCase().includes("mac");
      if ((isMac ? e.metaKey : e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      } else if (e.key === "Escape" && open) {
        setOpen(false);
        inputRef.current?.blur();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Click outside closes the dropdown.
  useEffect(() => {
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const moduleMatches = !debounced
    ? []
    : MODULE_INDEX.filter((m) => m.label.toLowerCase().includes(debounced.toLowerCase())).slice(0, 6);

  const go = (path, search) => {
    setOpen(false);
    setQ("");
    navigate(search ? `${path}?q=${encodeURIComponent(search)}` : path);
  };

  const totalHits = students.length + staff.length + moduleMatches.length;
  const showResults = open && debounced.length > 0;

  return (
    <div ref={boxRef} className="relative flex-1 max-w-2xl">
      <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
      <input
        ref={inputRef}
        data-testid="topbar-search"
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder="Search students, staff, modules…"
        className="w-full pl-10 pr-14 h-11 rounded-full border border-border bg-muted/40 text-sm placeholder:text-muted-foreground/80 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-0 focus:bg-background transition-colors"
      />
      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[10px] font-mono text-muted-foreground border border-border rounded px-1.5 py-0.5 pointer-events-none bg-card">
        ⌘K
      </span>

      {showResults && (
        <div
          data-testid="topbar-search-results"
          className="absolute left-0 right-0 top-full mt-1 z-40 bg-popover border border-border rounded-md shadow-lg max-h-[420px] overflow-y-auto"
        >
          {loading && (
            <div className="flex items-center justify-center gap-2 px-3 py-4 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Searching…
            </div>
          )}

          {!loading && totalHits === 0 && (
            <div className="px-3 py-6 text-center text-xs text-muted-foreground">
              No matches for <span className="font-mono">{debounced}</span>.
            </div>
          )}

          {students.length > 0 && (
            <ResultGroup label="Students">
              {students.map((s) => (
                <ResultRow
                  key={s.id}
                  testid={`search-result-student-${s.id}`}
                  icon={<GraduationCap className="h-3.5 w-3.5" />}
                  primary={`${s.first_name || ""} ${s.last_name || ""}`.trim() || s.admission_no}
                  secondary={s.admission_no}
                  onClick={() => go("/students", s.admission_no)}
                />
              ))}
            </ResultGroup>
          )}

          {staff.length > 0 && (
            <ResultGroup label="Staff">
              {staff.map((s) => (
                <ResultRow
                  key={s.id}
                  testid={`search-result-staff-${s.id}`}
                  icon={<UserCog className="h-3.5 w-3.5" />}
                  primary={`${s.first_name || ""} ${s.last_name || ""}`.trim() || s.email}
                  secondary={s.email}
                  onClick={() => go("/staff", s.email)}
                />
              ))}
            </ResultGroup>
          )}

          {moduleMatches.length > 0 && (
            <ResultGroup label="Jump to">
              {moduleMatches.map((m) => (
                <ResultRow
                  key={m.key}
                  testid={`search-result-module-${m.key}`}
                  icon={m.icon ? <m.icon className="h-3.5 w-3.5" /> : <ArrowRight className="h-3.5 w-3.5" />}
                  primary={m.label}
                  secondary={m.group}
                  onClick={() => go(m.path)}
                />
              ))}
            </ResultGroup>
          )}
        </div>
      )}
    </div>
  );
}

function ResultGroup({ label, children }) {
  return (
    <div className="py-1">
      <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground border-b border-border/60 bg-muted/30">
        {label}
      </div>
      {children}
    </div>
  );
}

function ResultRow({ icon, primary, secondary, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-accent hover:text-accent-foreground transition-colors text-sm"
    >
      <span className="text-muted-foreground">{icon}</span>
      <span className="flex-1 truncate">
        <span className="font-medium">{primary}</span>
        {secondary && <span className="ml-2 text-xs text-muted-foreground font-mono">{secondary}</span>}
      </span>
      <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
    </button>
  );
}
